/**
 * Google reCAPTCHA v3 Form Integration
 *
 * Automatically intercepts form submissions and adds reCAPTCHA tokens.
 * Tokens are generated for each form submission and attached as hidden fields.
 *
 * @package
 */

/**
 * Selector for forms that require reCAPTCHA validation.
 */
const RECAPTCHA_SELECTOR = 'data-recaptcha';

/**
 * Data attribute for explicitly defining the reCAPTCHA action name on a form.
 */
const RECAPTCHA_ACTION_DATA_ATTR = 'recaptchaAction';

/**
 * Handle form submission: generate token and attach to form.
 *
 * @param {Event} event - The form submit event.
 */
function handleFormSubmit ( event ) {
	const form = event.target;

	// Prevent default submission while we fetch the token.
	event.preventDefault();

	const siteKey = window.nsRecaptcha?.siteKey || '';
	if ( ! siteKey ) {
		// No site key, allow submission.
		form.submit();
		return;
	}

	const fieldName = window.nsRecaptcha?.tokenField || 'recaptcha_token';
	// If token already exists, allow submission.
	if ( form.querySelector( `[name="${fieldName}"]` ) ) {
		form.submit();
		return;
	}

	// Determine action name from form or use default.
	const action = form.dataset[ RECAPTCHA_ACTION_DATA_ATTR ] ?? 'default_recaptcha';

	// Generate token with Google.
	window.grecaptcha
		.execute( siteKey, { action } )
		.then( token => {
			// Create hidden field for token.
			const input = document.createElement( 'input' );
			input.type = 'hidden';
			input.name = fieldName;
			input.value = token;

			// Add to form and submit.
			form.appendChild( input );
			form.submit();
		} )
		.catch( () => {
			// Allow submission anyway on error (fail-open).
			form.submit();
		} );
}

/**
 * Initialize reCAPTCHA form handling.
 */
function init () {
	const siteKey = window.nsRecaptcha?.siteKey || '';
	const forms = document.querySelectorAll( `[${RECAPTCHA_SELECTOR}]` );

	if ( ! siteKey || forms.length === 0 ) {
		return;
	}

	forms.forEach( form => {
		form.addEventListener( 'submit', handleFormSubmit );
	} );
}

// Initialize when DOM is ready.
if ( document.readyState === 'loading' ) {
	document.addEventListener( 'DOMContentLoaded', init );
} else {
	init();
}
